<template>
  <div class="rc-searcher-container" v-click-outside="hide">
    <template v-if="floatLabel">
      <div
        class="rc-floating-label"
        :class="{
          'rc-label-active': isFocused || inputValue,
          'rc-label-centered': !isFocused && !inputValue,
        }"
        ref="floatingLabel"
        :style="stylesWithIcon"
      >
        <label for="" class="rc-floating-label__text">
          {{ label }}
        </label>
        <span
          v-show="requiredField"
          class="rc-required-field"
          ref="requiredField"
          >*</span
        >
      </div>
    </template>
    <div class="rc-searcher-input">
      <div class="rc-searcher-input__icon" v-if="hasIconSlot">
        <slot name="icon" class="icon" ref="icon-slot"></slot>
      </div>
      <div class="rc-form-group">
        <input
          v-model="inputValue"
          @blur="_onBlur($event)"
          @focus="_onFocus($event)"
          @input="typingHandler($event), onInputDebouncer($event)"
          @change="_onChange($event)"
          @keydown="pressedKeyHandler($event.key.toLowerCase())"
          :type="type"
          :min="min"
          :max="max"
          :disabled="disabled"
          :pattern="pattern"
          class="rc-searcher-field rc-default-input-styles"
          autocomplete="one-time-code"
        />
        <label
          v-if="!floatLabel && !inputValue"
          class="rc-form-group__label"
          :class="{ 'rc-form-group__label--required': requiredField }"
          >{{ label }}</label
        >
      </div>
      <div v-if="dropdown" class="rc-searcher-input__dropdown">
        <button
          class="rc-searcher-input__dropdown-button"
          @click.prevent="showSuggestions = !showSuggestions"
        >
          <svg class="ionicon" viewBox="0 0 500 500">
            <path
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="10"
              d="M112 184l144 144 144-144"
            />
          </svg>
        </button>
      </div>
      <div class="rc-searcher__spinner" v-if="loading">
        <div class="rc-searcher__spinner-icon"></div>
      </div>
    </div>
    <div
      class="rc-suggestions"
      v-if="localSuggestions.length && showSuggestions"
      @scroll="suggestionsScrollHandler($event)"
    >
      <slot name="suggestions-action"></slot>
      <ul
        class="rc-suggestions-list"
        ref="suggestionsList"
        v-for="(section, index) in localSuggestions"
        :key="'section' + index"
      >
        <slot name="section-title" :section="section">
          <p
            v-if="section.sectionName"
            class="rc-suggestions-list__section-name"
          >
            {{ section.sectionName }}
          </p>
        </slot>
        <li
          class="rc-suggestions-list__item"
          @mousedown.stop="selectSuggestion(suggestion)"
          v-for="(suggestion, index) in section.options"
          :key="index"
        >
          <slot name="suggestion-item" :suggestion="suggestion">
            <span>
              {{ suggestion.label }}
            </span>
          </slot>
        </li>
        <hr
          v-if="index < localSuggestions.length - 1"
          class="rc-suggestions-list__divider"
        />
        <slot name="loading-container"> </slot>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: "RCSearcher",
  directives: {
    "click-outside": {
      bind: (el, binding, vnode) => {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            vnode.context[binding.expression](event);
          }
        };
        document.body.addEventListener("click", el.clickOutsideEvent);
      },
      unbind: (el) => {
        document.body.removeEventListener("click", el.clickOutsideEvent);
      },
    },
  },
  props: {
    value: {
      required: false,
    },
    label: String,
    floatLabel: Boolean,
    type: {
      type: String,
      default: "text",
    },
    delay: {
      type: Number,
      default: 300,
    },
    requestLength: {
      type: Number,
      default: 1,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: Number,
    disabled: {
      type: Boolean,
      default: false,
    },
    pattern: {
      type: String,
      required: false,
    },
    suggestions: {
      type: Array,
      required: false,
      default: () => [],
    },
    textField: {
      type: String,
    },
    forceSelection: {
      type: Boolean,
      default: false,
    },
    dropdown: {
      type: Boolean,
      default: false,
    },
    requiredField: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      inputValue: this.value || "",
      localSuggestions: [],
      isFocused: false,
      showSuggestions: false,
      onInputDebouncer: null,
      loading: false,
      selectedSuggestion: null,
    };
  },
  watch: {
    isFocused(newValue) {
      if (!newValue) {
        this.loading = false;
      }
    },
    value: {
      immediate: true,
      handler(newValue) {
        if (!newValue) {
          this.inputValue = "";
          this.selectedSuggestion = null;
          return;
        }
        this.selectedSuggestion = JSON.parse(JSON.stringify(newValue));
      },
    },
    suggestions: {
      deep: true,
      handler() {
        this.localSuggestions = JSON.parse(JSON.stringify(this.suggestions));
        if (!this.localSuggestions.length) {
          return;
        }
        const thereAreOptions = this.optionsLengthValidation();
        if (!thereAreOptions) {
          this.loading = false;
          return;
        }
        this.showSuggestions = true;
        this.loading = false;
      },
    },
    selectedSuggestion: {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.inputValueHandler(newValue);
      },
    },
  },
  computed: {
    hasIconSlot() {
      return !!this.$slots.icon;
    },
    stylesWithIcon() {
      if (this.isFocused || this.inputValue) {
        return "left: 5px";
      }
      if (this.$slots.icon) {
        return "left: 28px";
      }
      return "left: 5px";
    },
  },
  created() {
    this.localSuggestions = JSON.parse(JSON.stringify(this.suggestions));
    this.onInputDebouncer = this.debounce(
      (payload) => this._onInput(payload),
      this.delay
    );
  },
  mounted() {
    this.$nextTick(() => {
      if (this.floatLabel) {
        const floatLabelContainer = this.$refs.floatingLabel;
        if (this.floatLabel) {
          floatLabelContainer.style.width = `60%`;
        }
      }
    });
  },
  methods: {
    inputValueHandler(newValue) {
      if (!newValue) {
        return;
      }
      if (typeof newValue === "string") {
        this.inputValue = newValue;
        return;
      }
      if (newValue && this.textField in newValue) {
        this.inputValue = newValue[this.textField];
      }
    },
    typingHandler(event) {
      this.selectedSuggestion = null;
      this.localSuggestions = [];
      const query = event.target.value;
      if (!query || query.length < this.requestLength) {
        this.loading = false;
        return;
      }
      this.loading = true;
    },
    _onInput(event) {
      const query = event.target.value;
      if (!query || query.length < this.requestLength) {
        return;
      }
      this.$emit("search", {
        query: query,
        originalEvent: event,
      });
    },
    _onChange(event) {
      this.$emit("change", event.target.value);
    },
    _onBlur(event) {
      this.$emit("blur", event.target.value);
      this.forceSelectionHandler();
      this.isFocused = false;
      this.showSuggestions = false;
    },
    _onFocus(event) {
      const thereAreOptions = this.optionsLengthValidation();
      if (this.localSuggestions.length > 0 && thereAreOptions) {
        this.showSuggestions = true;
      }
      this.$emit("focus", event.target.value);
      this.isFocused = true;
    },
    forceSelectionHandler() {
      if (this.forceSelection && !this.selectedSuggestion) {
        this.inputValue = "";
        this.localSuggestions = [];
        this.$emit("input", null);
      }
    },
    selectSuggestion(suggestion) {
      this.selectedSuggestion = suggestion;
      this.$emit("input", suggestion);
      this.$emit("selectOption", suggestion);
      if (!this.dropdown) {
        this.localSuggestions = [];
      }
      this.showSuggestions = false;
    },
    pressedKeyHandler(key) {
      if (key === "backspace") {
        this.selectedSuggestion = null;
      }
    },
    optionsLengthValidation() {
      return this.localSuggestions.some((item) => item.options.length > 0);
    },
    hide() {
      this.showSuggestions = false;
    },
    suggestionsScrollHandler(event) {
      const { scrollTop, clientHeight, scrollHeight } = event.target;
      if (Math.ceil(scrollTop + clientHeight) >= scrollHeight) {
        this.$emit("bottomReached");
      }
    },
    debounce(fn, wait) {
      let timer;
      return function (...args) {
        if (timer) {
          clearTimeout(timer);
        }
        const context = this;
        timer = setTimeout(() => {
          fn.apply(context, args);
        }, wait);
      };
    },
  },
};
</script>

<style scoped>
@import "../styles/input.css";

.rc-searcher-container {
  background-color: white;
  position: relative;
  display: flex;
  align-items: center;
  width: 200px;
  height: 40px;
  transition: all 0.3s;
  font-size: 16px;
  color: #888;
  border: 1px solid #939393;
  border-radius: 10px;
}

.rc-searcher-input {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
}

.rc-searcher-input__dropdown {
  width: 40px;
  display: flex;
  align-items: center;
}

.rc-searcher-input__dropdown-button {
  width: 100%;
  padding-left: 2px;
  background: none;
  border: none;
  border-left: 1px solid #939393;
}

.rc-searcher-input__icon {
  padding: 0px 0px 0px 5px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.rc-searcher-field {
  font-size: 16px;
  height: 100%;
  background: none;
}

.rc-searcher-field:after {
  content: "*";
  position: absolute;
  top: 3px;
  right: 0px;
  color: #f00;
}

.rc-label-active {
  font-size: 12px;
  left: 5px;
  transform: translateY(-18px);
}

/* .rc-label-centered {
  width: 90%;
  top: 52%;
  left: 5px;
  transform: translateY(-50%);
  text-overflow: ellipsis;
  overflow: hidden;
} */

.hover-item-background {
  background-color: #e9ecef;
}

.rc-suggestions {
  position: absolute;
  top: 40px;
  left: 0px;
  width: 100%;
  max-height: 200px;
  overflow-y: scroll;
  border: 1px solid rgba(196, 196, 196, 0.767);
  background-color: white;
  z-index: 10;
}

.rc-suggestions-list {
  padding: 0;
  margin: 0;
  list-style-type: none;
  text-align: left;
  overflow-x: hidden;
}

.rc-suggestions-list__item {
  padding: 5px 10px;
  cursor: pointer;
}

.rc-suggestions-list__item:hover {
  background-color: #ededed;
}

.rc-suggestions-list__section-name {
  margin: 15px 0px 0px 10px;
  font-size: 0.9rem;
  text-align: left;
  font-weight: bold;
}

.rc-suggestions-list__divider {
  margin: 0px 5px 0px 5px;
}

.rc-searcher__spinner {
  color: #737373;
  position: absolute;
  top: 10px;
  right: 2%;
}

.rc-searcher__spinner-icon {
  height: 16px;
  width: 16px;
  color: #5a5a5a;
  position: relative;
  display: inline-block;
  border: 3px solid;
  border-radius: 50%;
  border-top-color: transparent;
  animation: rotate 1s linear infinite;
}

.rc-suggestions::-webkit-scrollbar {
  width: 5px;
}
.rc-suggestions::-webkit-scrollbar-track {
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  border-radius: 10px;
}
.rc-suggestions::-webkit-scrollbar-thumb {
  border-radius: 10px;
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}
</style>
